/**
 * PWA Store - Pinia
 *
 * Manages PWA lifecycle: install prompt, service worker updates, offline status.
 * Handles beforeinstallprompt event and provides update notifications.
 */

import { defineStore } from 'pinia';

export const usePwaStore = defineStore('pwa', {
  state: () => ({
    // Install prompt
    deferredPrompt: null,
    canInstall: false,
    isInstalled: false,

    // Update state
    updateAvailable: false,
    registration: null,

    // Offline state
    isOffline: !navigator.onLine
  }),

  actions: {
    /**
     * Initialize PWA event listeners
     * Should be called once in App.vue on mount
     */
    init () {
      // Listen for install prompt
      window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);

      // Listen for successful install
      window.addEventListener('appinstalled', this.handleAppInstalled);

      // Listen for online/offline status
      window.addEventListener('online', () => { this.isOffline = false; });
      window.addEventListener('offline', () => { this.isOffline = true; });

      // Check if app is already installed (standalone mode)
      this.checkIfInstalled();

      // Register service worker and listen for updates
      this.registerServiceWorker();
    },

    /**
     * Clean up event listeners
     */
    cleanup () {
      window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', this.handleAppInstalled);
    },

    /**
     * Handle beforeinstallprompt event
     */
    handleBeforeInstallPrompt (event) {
      // Prevent Chrome 67+ from showing the mini-infobar
      event.preventDefault();
      // Stash the event so it can be triggered later
      this.deferredPrompt = event;
      this.canInstall = true;
    },

    /**
     * Handle successful app installation
     */
    handleAppInstalled () {
      this.deferredPrompt = null;
      this.canInstall = false;
      this.isInstalled = true;
    },

    /**
     * Check if app is running in standalone mode (already installed)
     */
    checkIfInstalled () {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
      this.isInstalled = isStandalone;
    },

    /**
     * Prompt user to install the app
     */
    async promptInstall () {
      if (!this.deferredPrompt) return false;

      // Show the install prompt
      this.deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await this.deferredPrompt.userChoice;

      // Clear the deferred prompt - it can only be used once
      this.deferredPrompt = null;
      this.canInstall = false;

      return outcome === 'accepted';
    },

    /**
     * Dismiss the install prompt without installing
     */
    dismissInstall () {
      this.canInstall = false;
      // Keep deferredPrompt in case user wants to install later via menu
    },

    /**
     * Register service worker and set up update detection
     * Only runs in production (SW file is generated at build time)
     */
    async registerServiceWorker () {
      if (!('serviceWorker' in navigator)) return;

      // Skip in development - SW file only exists after build
      if (import.meta.env.DEV) {
        await this.cleanupDevServiceWorkers();
        console.info('[PWA] Skipping service worker registration in development');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });

        this.registration = registration;

        // Check for updates immediately
        registration.update();

        // If a worker is already waiting (e.g. user reopened app), show update prompt.
        if (registration.waiting) {
          this.updateAvailable = true;
        }

        // Listen for new service worker waiting to activate
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New update available
                this.updateAvailable = true;
              }
            });
          }
        });

        // Listen for controller change (new SW took over)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // Only reload if we were showing update available
          if (this.updateAvailable) {
            this.updateAvailable = false;
            window.location.reload();
          }
        });
      }
      catch (error) {
        console.warn('[PWA] Service worker registration failed:', error);
      }
    },

    /**
     * Apply pending update by telling SW to skip waiting
     */
    applyUpdate () {
      if (!this.registration?.waiting) return;

      // Tell the waiting service worker to take over
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    },

    /**
     * Dismiss update notification
     */
    dismissUpdate () {
      this.updateAvailable = false;
    },

    /**
     * Dev-only hygiene: remove stale SWs/caches that can serve outdated hashed assets.
     */
    async cleanupDevServiceWorkers () {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (!registrations.length) return;

        await Promise.all(registrations.map(registration => registration.unregister()));

        let deletedCaches = 0;
        if ('caches' in window) {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map(key => caches.delete(key)));
          deletedCaches = cacheKeys.length;
        }

        console.info(`[PWA] Development cleanup removed ${registrations.length} service worker(s) and ${deletedCaches} cache(s).`);
      }
      catch (error) {
        console.warn('[PWA] Development service worker cleanup failed:', error);
      }
    }
  }
});
